import { Checkbox, FormControlLabel, Grid, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { merge, Subject } from 'rxjs';
import { debounceTime, filter, tap } from 'rxjs/operators';
import { RootState } from '../../store';
import { getSelectedUserGroup } from './redux/userGroupSelectors';
import { updateUserGroup } from './redux/userGroupSlices';
const mapStateToProps = (state: RootState) => ({
    selectedUserGroup: getSelectedUserGroup(state)
});

const nameMaxLength = 60;
const descrMaxLength = 200;

const nameSubject = new Subject<string>();
function onNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    nameSubject.next(e.target.value);
}
const descrSubject = new Subject<string>();
function onDescrChange(e: React.ChangeEvent<HTMLInputElement>) {
    descrSubject.next(e.target.value);
}
const disableAgreementEmailSubject = new Subject<boolean>();
function onDisableAgreementEmailChange(checked: boolean) {
    disableAgreementEmailSubject.next(checked);
}

const hideWarehouseBalanceSubject = new Subject<boolean>();
function onHideWarehouseBalanceChange(checked: boolean) {
    hideWarehouseBalanceSubject.next(checked);
}

type Props = ReturnType<typeof mapStateToProps>;
const ManageGroupGeneral = ({ selectedUserGroup }: Props) => {
    const dispatch = useDispatch();

    const [currName, setCurrName] = useState('');
    const [currDescr, setCurrDescr] = useState('');
    const [currDisableAgreementEmail, setCurrDisableAgreementEmail] = useState(false);
    const [currHideWarehouseBalance, setCurrHideWarehouseBalance] = useState(false);
    const [errors, setErrors] = useState({
        name: null as string | null,
        description: null as string | null,
        disableAgreementEmail: null as string | null,
        hideWarehouseBalance: null as string | null
    });

    useEffect(() => {
        if (selectedUserGroup == null) {
            return;
        }

        setCurrName(selectedUserGroup.name);
        setCurrDescr(selectedUserGroup.description);
        setCurrDisableAgreementEmail(selectedUserGroup.disableAgreementEmail);
        setCurrHideWarehouseBalance(selectedUserGroup.hideWarehouseBalance);

        const submit = (
            name: string,
            description: string,
            disableAgreementEmail: boolean,
            hideWarehouseBalance: boolean
        ) => {
            dispatch(
                updateUserGroup.request({
                    userGroupId: selectedUserGroup.userGroupId,
                    dto: {
                        name: name,
                        description: description,
                        disableInactivateArticleEmails: disableAgreementEmail,
                        hideWarehouseBalance: hideWarehouseBalance
                    }
                })
            );
        };

        const subscription = merge(
            nameSubject.pipe(
                filter(name => {
                    setCurrName(name);
                    if (name.length === 0) {
                        setErrors(errs => ({ ...errs, name: "Name can't be empty" }));
                        return false;
                    }
                    if (name.length > nameMaxLength) {
                        setErrors(errs => ({ ...errs, name: `Maximum ${nameMaxLength} characters.` }));
                        return false;
                    }

                    setErrors(errs => ({ ...errs, name: null }));

                    return true;
                }),
                debounceTime(1000),
                tap(name =>
                    submit(
                        name,
                        selectedUserGroup.description,
                        selectedUserGroup.disableAgreementEmail,
                        selectedUserGroup.hideWarehouseBalance
                    )
                )
            ),
            descrSubject.pipe(
                filter(description => {
                    setCurrDescr(description);
                    if (description.length > descrMaxLength) {
                        setErrors(errs => ({
                            ...errs,
                            description: `Maximum ${descrMaxLength} characters.`
                        }));
                        return false;
                    }

                    setErrors(errs => ({
                        ...errs,
                        description: null
                    }));

                    return true;
                }),
                debounceTime(1000),
                tap(descr =>
                    submit(
                        selectedUserGroup.name,
                        descr,
                        selectedUserGroup.disableAgreementEmail,
                        selectedUserGroup.hideWarehouseBalance
                    )
                )
            ),
            disableAgreementEmailSubject.pipe(
                filter(disableAgreementEmail => {
                    setCurrDisableAgreementEmail(disableAgreementEmail);
                    return true;
                }),
                debounceTime(1000),
                tap(disableAgreementEmail =>
                    submit(
                        selectedUserGroup.name,
                        selectedUserGroup.description,
                        disableAgreementEmail,
                        selectedUserGroup.hideWarehouseBalance
                    )
                )
            ),
            hideWarehouseBalanceSubject.pipe(
                filter(hideWarehouseBalance => {
                    setCurrHideWarehouseBalance(hideWarehouseBalance);
                    return true;
                }),
                debounceTime(1000),
                tap(hideWarehouseBalance =>
                    submit(
                        selectedUserGroup.name,
                        selectedUserGroup.description,
                        selectedUserGroup.disableAgreementEmail,
                        hideWarehouseBalance
                    )
                )
            )
        ).subscribe();

        // Important to unsubscribe on teardown so we don't create a memory leak
        return () => subscription.unsubscribe();
    }, [dispatch, selectedUserGroup]);

    if (selectedUserGroup == null) {
        return <div />;
    }

    return (
        <Grid container direction="column" spacing={2} sx={{ maxWidth: 600 }}>
            <Grid item>
                <TextField
                    label="Name"
                    value={currName}
                    onChange={onNameChange}
                    error={errors.name != null}
                    helperText={errors.name ?? ''}
                    variant="standard"
                />
            </Grid>
            <Grid item>
                <TextField
                    label="Description"
                    value={currDescr}
                    onChange={onDescrChange}
                    error={errors.description != null}
                    helperText={errors.description ?? ''}
                    fullWidth
                    variant="standard"
                />
            </Grid>
            <Grid item>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={currDisableAgreementEmail}
                            onChange={(_, checked) => onDisableAgreementEmailChange(checked)}
                        />
                    }
                    label="Disable inactivate agreement product mail"
                />
            </Grid>
            <Grid item>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={currHideWarehouseBalance}
                            onChange={(_, checked) => onHideWarehouseBalanceChange(checked)}
                        />
                    }
                    label="Hide warehouse balance"
                />
            </Grid>
        </Grid>
    );
};

export default connect(mapStateToProps)(ManageGroupGeneral);
